import { Head, Link, useForm, router } from "@inertiajs/react"
import { ArrowLeft, Upload, ExternalLink, MapPin, Plus, Pencil, Trash2, MailX } from "lucide-react"
import { useState, useRef } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CertificationsCard, QuizProgressCard } from "@/components/certification/CertificationCards"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { VoiceSelector } from "@/components/podcast/VoiceSelector"
import { ArticleCombobox } from "@/components/admin/ArticleCombobox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { COUNTRIES } from "@/lib/countries"

const STATUS_BADGES = {
  draft: { label: "Draft", className: "bg-zinc-100 text-zinc-700" },
  pending_review: { label: "Pending Review", className: "bg-amber-100 text-amber-800" },
  published: { label: "Published", className: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
}

function AdminLocationDialog({ open, onOpenChange, personId, location = null }) {
  const isEditing = !!location
  const form = useForm({
    city: location?.city || "",
    state: location?.state || "",
    country: location?.country || "US",
    zip_code: location?.zipCode || "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      form.patch(`/app/admin/people/${personId}/locations/${location.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post(`/app/admin/people/${personId}/locations`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Location" : "Add Location"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing ? "Update this location's details." : "Add a coaching location."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="admin-loc-country">Country</Label>
              <select
                id="admin-loc-country"
                value={form.data.country}
                onChange={(e) => {
                  const newCountry = e.target.value
                  if (newCountry !== form.data.country) {
                    form.setData((prev) => ({
                      ...prev,
                      country: newCountry,
                      state: "",
                      zip_code: "",
                    }))
                  }
                }}
                className="flex h-9 w-full rounded-md border border-zinc-300 bg-white px-3 py-1 text-sm text-zinc-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="admin-loc-city">City</Label>
                <Input
                  id="admin-loc-city"
                  placeholder="Indianapolis"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.city}
                  onChange={(e) => form.setData("city", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-loc-state">
                  {form.data.country === "US" ? "State" : "State / Province"}
                </Label>
                <Input
                  id="admin-loc-state"
                  placeholder={form.data.country === "US" ? "IN" : "Province"}
                  {...(form.data.country === "US" ? { maxLength: 2 } : {})}
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.state}
                  onChange={(e) =>
                    form.setData("state", form.data.country === "US" ? e.target.value.toUpperCase() : e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-loc-zip">
                {form.data.country === "US" ? "Zip Code" : "Postal Code"}
              </Label>
              <Input
                id="admin-loc-zip"
                placeholder={form.data.country === "US" ? "46204" : "Postal code"}
                {...(form.data.country === "US" ? { maxLength: 5 } : {})}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.zip_code}
                onChange={(e) => form.setData("zip_code", e.target.value)}
              />
              {form.errors.zip_code && (
                <p className="text-sm text-red-600">{form.errors.zip_code}</p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Saving..." : isEditing ? "Save Changes" : "Add Location"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function PersonShow({
  currentUser,
  person,
  voices = [],
  profileArticleOptions = [],
  articles = [],
  pendingCount,
  certifications = [],
  quizProgress = [],
  quizAnswers = null,
}) {
  const [headshotPreview, setHeadshotPreview] = useState(null)
  const headshotInputRef = useRef(null)
  const [locationDialogOpen, setLocationDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)
  const [deleteLocation, setDeleteLocation] = useState(null)

  const { data, setData, patch, processing, errors } = useForm({
    name: person.name || "",
    email: person.email || "",
    role: person.role || "guest",
    is_admin: person.isAdmin || false,
    is_beta_tester: person.isBetaTester || false,
    bio: person.bio || "",
    company: person.company || "",
    job_title: person.jobTitle || "",
    is_virtual: person.isVirtual || false,
    is_international: person.isInternational || false,
    marketing_active: person.marketingActive || false,
    writing_guidelines: person.writingGuidelines || "",
    headshot: null,
    schedule_call_url: person.scheduleCallUrl || "",
    ghl_location_id: person.ghlLocationId || "",
    profile_article_id: person.profileArticleId?.toString() || "",
    name_phonetic: person.namePhonetic || "",
    favorite_podcast_style: person.favoritePodcastStyle || "",
    favorite_host_voice_id: person.favoriteHostVoiceId?.toString() || "",
    favorite_interviewee_voice_id:
      person.favoriteIntervieweeVoiceId?.toString() || "",
    _source: "show",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/app/admin/people/${person.id}`, {
      preserveScroll: true,
      forceFormData: true,
    })
  }

  const handleHeadshotChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setData("headshot", file)
      setHeadshotPreview(URL.createObjectURL(file))
    }
  }

  const currentHeadshotUrl = headshotPreview || person.headshotUrl

  const isCoach = data.role === "coach"

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title={`${person.name || person.email} - People - Kingdom Factor Admin`} />

      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <Link
            href="/app/admin/people"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to People
          </Link>

          <div>
            <h1 className="text-2xl font-bold text-zinc-900">
              Manage {data.name || person.email}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Edit all settings for this person.
            </p>
          </div>
        </div>

        {/* Broadcast opt-out status (U9). Broadcast-only — never affects login email. */}
        {person.unsubscribedAt && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <MailX className="h-4 w-4 flex-shrink-0" />
              <span>
                Unsubscribed from broadcasts on{" "}
                {new Date(person.unsubscribedAt).toLocaleDateString()}. They still receive
                account &amp; login emails.
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                router.post(
                  `/app/admin/people/${person.id}/resubscribe`,
                  {},
                  { preserveScroll: true }
                )
              }
            >
              Resubscribe
            </Button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Headshot Upload */}
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center flex-shrink-0 border-2 border-zinc-200">
                  {currentHeadshotUrl ? (
                    <img
                      src={currentHeadshotUrl}
                      alt="Profile photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-display text-zinc-400">
                      {(person.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => headshotInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </Button>
                  <input
                    ref={headshotInputRef}
                    type="file"
                    accept="image/webp,image/png,image/jpeg"
                    onChange={handleHeadshotChange}
                    className="hidden"
                  />
                  <p className="text-xs text-zinc-500">
                    Square photo recommended (400x400px). Max 5MB.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    className="bg-white border-zinc-300 text-zinc-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.email}
                    onChange={(e) => setData("email", e.target.value)}
                    className="bg-white border-zinc-300 text-zinc-900"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={data.company}
                    onChange={(e) => setData("company", e.target.value)}
                    className="bg-white border-zinc-300 text-zinc-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={data.job_title}
                    onChange={(e) => setData("job_title", e.target.value)}
                    className="bg-white border-zinc-300 text-zinc-900"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name_phonetic">Name Pronunciation</Label>
                <Input
                  id="name_phonetic"
                  value={data.name_phonetic}
                  onChange={(e) => setData("name_phonetic", e.target.value)}
                  placeholder="e.g., KLEH-ven-jer"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                />
                <p className="text-xs text-zinc-500">
                  Phonetic spelling so the AI narrator pronounces the name correctly.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={data.bio}
                  onChange={(e) => setData("bio", e.target.value)}
                  rows={3}
                  placeholder="A short bio for the author profile"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                />
              </div>
            </CardContent>
          </Card>

          {/* Admin Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Admin</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={data.role}
                  onValueChange={(value) => setData("role", value)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300 text-zinc-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="guest">Guest</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-sm font-medium text-red-600">{errors.role}</p>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_admin" className="cursor-pointer">
                    Admin Access
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Grant admin privileges to this person
                  </p>
                </div>
                <Switch
                  id="is_admin"
                  checked={data.is_admin}
                  onCheckedChange={(checked) => setData("is_admin", checked)}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_beta_tester" className="cursor-pointer">
                    Beta Tester
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Grants access to in-development features (member portal, coach User Management & Content tabs)
                  </p>
                </div>
                <Switch
                  id="is_beta_tester"
                  checked={data.is_beta_tester}
                  onCheckedChange={(checked) => setData("is_beta_tester", checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Coach Settings — only visible when role is coach */}
          {isCoach && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Coach Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="schedule_call_url">Schedule a Call Link</Label>
                    <Input
                      id="schedule_call_url"
                      value={data.schedule_call_url}
                      onChange={(e) => setData("schedule_call_url", e.target.value)}
                      placeholder="https://your-calendar-link.com"
                      className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                    />
                    <p className="text-xs text-zinc-500">
                      HighLevel calendar link, Calendly, or any scheduling URL. Appears on articles and coach profile.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ghl_location_id">CRM Location ID</Label>
                    <Input
                      id="ghl_location_id"
                      value={data.ghl_location_id}
                      onChange={(e) => setData("ghl_location_id", e.target.value)}
                      placeholder="e.g. wIXRAOC2zIc1yTAkGSu6"
                      className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                    />
                    <p className="text-xs text-zinc-500">
                      GoHighLevel sub-account location ID. Required for CRM SSO.
                    </p>
                  </div>

                  {profileArticleOptions.length > 0 && (
                    <div className="space-y-2">
                      <Label>Profile Article</Label>
                      <ArticleCombobox
                        articles={profileArticleOptions}
                        value={data.profile_article_id}
                        onSelect={(id) => setData("profile_article_id", id)}
                        placeholder="Select a profile article"
                        emptyLabel="No profile article"
                      />
                      <p className="text-xs text-zinc-500">
                        The coach's self-authored profile article, linked from the coaches directory.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="writing_guidelines">
                      AI Writing Guidelines
                    </Label>
                    <Textarea
                      id="writing_guidelines"
                      value={data.writing_guidelines}
                      onChange={(e) =>
                        setData("writing_guidelines", e.target.value)
                      }
                      rows={6}
                      placeholder="e.g., Always use active voice. Keep paragraphs short (2-3 sentences)."
                      className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                    />
                    <p className="text-xs text-zinc-500">
                      {data.writing_guidelines?.length || 0}/5,000 characters.
                      Guides the AI assistant to match this coach's voice and style.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Marketing Program */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Marketing Program</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="marketing_active" className="cursor-pointer">
                        Enrolled in Marketing Program
                      </Label>
                      <p className="text-xs text-zinc-500">
                        When enabled, this coach's GHL pipeline data will be synced nightly for reporting.
                      </p>
                    </div>
                    <Switch
                      id="marketing_active"
                      checked={data.marketing_active}
                      onCheckedChange={(checked) => setData("marketing_active", checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Podcast Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Podcast Defaults</CardTitle>
                  <p className="text-sm text-zinc-500">
                    Pre-filled when generating a podcast for any of this coach's articles.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Preferred Podcast Style</Label>
                    <Select
                      value={data.favorite_podcast_style}
                      onValueChange={(value) =>
                        setData("favorite_podcast_style", value)
                      }
                    >
                      <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                        <SelectValue placeholder="Select style" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interview">Interview</SelectItem>
                        <SelectItem value="deep_dive">Deep Dive</SelectItem>
                        <SelectItem value="solo">Solo Narration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <VoiceSelector
                      label="Preferred Host Voice"
                      voices={voices}
                      value={data.favorite_host_voice_id}
                      onChange={(id) => setData("favorite_host_voice_id", id)}
                    />
                  </div>

                  {data.favorite_podcast_style !== "solo" && (
                    <div>
                      <VoiceSelector
                        label="Preferred Interviewee / Second Host Voice"
                        voices={voices}
                        value={data.favorite_interviewee_voice_id}
                        onChange={(id) =>
                          setData("favorite_interviewee_voice_id", id)
                        }
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Locations */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Locations</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingLocation(null)
                      setLocationDialogOpen(true)
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Location
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {person.locations?.length > 0 ? (
                    <div className="space-y-2">
                      {person.locations.map((loc) => (
                        <div
                          key={loc.id}
                          className="flex items-center justify-between rounded-lg border border-zinc-200 p-3"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-zinc-900">
                                {[loc.city, loc.state].filter(Boolean).join(", ") || "No city/state"}
                                {loc.country && loc.country !== "US" && ` (${loc.country})`}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {loc.zipCode || "No zip code"}
                                {loc.isGeocoded && (
                                  <span className="text-green-600 ml-2">Geocoded</span>
                                )}
                                {loc.zipCode && !loc.isGeocoded && (
                                  <span className="text-amber-600 ml-2">Not geocoded</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingLocation(loc)
                                setLocationDialogOpen(true)
                              }}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteLocation(loc)}
                              title="Delete"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-zinc-400">
                      <MapPin className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                      <p className="text-sm font-medium text-zinc-500">No locations</p>
                      <p className="text-xs">Add locations for this coach's in-person groups.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Availability Flags */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Availability Flags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="is_virtual" className="cursor-pointer">
                        Virtual Coach
                      </Label>
                      <p className="text-xs text-zinc-500">
                        Available as fallback for prospects outside local range
                      </p>
                    </div>
                    <Switch
                      id="is_virtual"
                      checked={data.is_virtual}
                      onCheckedChange={(checked) =>
                        setData("is_virtual", checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="is_international"
                        className="cursor-pointer"
                      >
                        International Coach
                      </Label>
                      <p className="text-xs text-zinc-500">
                        Receives leads from prospects outside the United States
                      </p>
                    </div>
                    <Switch
                      id="is_international"
                      checked={data.is_international}
                      onCheckedChange={(checked) =>
                        setData("is_international", checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button variant="gradient" type="submit" disabled={processing}>
              {processing ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>

        {/* Ascend Coach Certification (slice 5) — outside the form: the reset
            button posts on its own. */}
        <CertificationsCard certifications={certifications} />
        <QuizProgressCard quizProgress={quizProgress} accountId={person.id} quizAnswers={quizAnswers} />

        {/* Articles */}
        {articles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Articles ({articles.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {articles.map((article) => {
                    const status = STATUS_BADGES[article.status] || STATUS_BADGES.draft
                    const date = article.publishedAt || article.createdAt
                    const formattedDate = date
                      ? new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"

                    return (
                      <TableRow key={article.id}>
                        <TableCell className="font-medium">
                          {article.title}
                        </TableCell>
                        <TableCell className="text-zinc-500">
                          {article.category || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={status.className}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-500">
                          {formattedDate}
                        </TableCell>
                        <TableCell>
                          {article.status === "published" && article.categorySlug && (
                            <Button variant="ghost" size="sm" asChild>
                              <Link href={`/collective/${article.categorySlug}/${article.slug}`}>
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Location Dialog */}
      {isCoach && (
        <>
          <AdminLocationDialog
            key={editingLocation?.id || "new"}
            open={locationDialogOpen}
            onOpenChange={setLocationDialogOpen}
            personId={person.id}
            location={editingLocation}
          />

          <AlertDialog
            open={!!deleteLocation}
            onOpenChange={(open) => !open && setDeleteLocation(null)}
          >
            <AlertDialogContent className="bg-white border-zinc-200">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Location</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to remove{" "}
                  <strong>
                    {deleteLocation && [deleteLocation.city, deleteLocation.state].filter(Boolean).join(", ")}
                  </strong>
                  ? This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    router.delete(`/app/admin/people/${person.id}/locations/${deleteLocation.id}`, {
                      preserveScroll: true,
                      onSuccess: () => setDeleteLocation(null),
                    })
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </AdminLayout>
  )
}
